import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Icon, XTNDLogo, XTNDLogoDark } from '@repo/ui';
import { showToast } from '../utils/toast';
import { Button, Card, CardHeader, CardBody } from '../components/ui';
import { useSocialLogin, useOAuthCallback } from '../hooks/useAuth';
import { useDarkMode } from '../hooks/useDarkMode';
import { authService } from '../services/auth';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isDark } = useDarkMode();
  const hasProcessedCallback = useRef(false);

  // Get the intended destination from location state
  const from = location.state?.from?.pathname || '/';

  // Check if already authenticated and redirect
  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate(from, { replace: true });
    }
  }, [navigate, from]);

  // Initialize hooks
  const socialLoginMutation = useSocialLogin();
  const oauthCallbackMutation = useOAuthCallback();

  // Handle OAuth callback
  useEffect(() => {
    if (hasProcessedCallback.current) {
      return;
    }

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    if (error) {
      hasProcessedCallback.current = true;
      showToast.error(`Authentication failed: ${error}`);
      return;
    }

    if (code && state) {
      hasProcessedCallback.current = true;
      oauthCallbackMutation.mutate(
        { code, state },
        {
          onSuccess: () => {
            navigate(from, { replace: true });
          },
          onError: () => {
            // Clear URL parameters on error
            navigate('/login', { replace: true });
          },
        }
      );
    }
  }, [searchParams, oauthCallbackMutation, navigate, from]);

  const handleSocialLogin = () => {
    socialLoginMutation.mutate({});
  };

  // Determine loading state
  const isLoading = socialLoginMutation.isPending || oauthCallbackMutation.isPending;

  return (
    <div className="min-h-screen bg-gradient-to-br from-xtnd-dark-800 via-xtnd-dark-900 to-xtnd-dark-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-xtnd-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-xtnd-light-300 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 w-full max-w-lg">
        {/* Header Section */}
        <div className="text-center mb-10">
          {/* Logo Container with theme-aware logo */}
          <div className="flex justify-center mb-6">
            <div className="bg-white dark:bg-xtnd-dark-800 rounded-2xl p-6 shadow-2xl border border-xtnd-dark-200 dark:border-xtnd-dark-600">
              {isDark ? <XTNDLogoDark size="2xl" /> : <XTNDLogo size="2xl" />}
            </div>
          </div>

          {/* Brand Title */}
          <div className="space-y-2">
            <h1 className="text-4xl font-bold text-white drop-shadow-2xl tracking-tight">
              XTND Juniper Admin
            </h1>
            <p className="text-xtnd-dark-200 text-lg font-medium drop-shadow-xl">
              System Administration Panel
            </p>
          </div>
        </div>

        {/* Login Card */}
        <Card variant="default" className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
          <CardHeader className="text-center pb-8 pt-8">
            <h2 className="text-2xl font-bold text-xtnd-dark-900 mb-2">
              Administrator Access
            </h2>
            <p className="text-xtnd-dark-500 text-base">
              Sign in to access the system administration panel
            </p>
          </CardHeader>

          <CardBody className="px-8 pb-8">
            <div className="space-y-6">
              {/* Social Login Buttons */}
              <div className="space-y-4">
                <Button
                  type="button"
                  variant="social"
                  size="lg"
                  onClick={handleSocialLogin}
                  disabled={isLoading}
                  fullWidth
                  leftIcon={<Icon name="GoogleIcon" size="md" />}
                >
                  Continue with Google
                </Button>
              </div>

              {/* Loading State */}
              {isLoading && (
                <div className="flex items-center justify-center py-6 border-t border-xtnd-dark-100">
                  <div className="flex items-center text-xtnd-dark-800">
                    <Icon name="LoadingSpinner" size="md" className="-ml-1 mr-3" />
                    <span className="text-base font-medium">Redirecting to login provider...</span>
                  </div>
                </div>
              )}

              {/* Security Notice */}
              <div className="pt-4 border-t border-xtnd-dark-100">
                <div className="flex items-center justify-center text-center">
                  <div className="flex items-center text-sm text-xtnd-dark-500">
                    <Icon
                      name="SecurityIcon"
                      size="sm"
                      className="mr-2 text-xtnd-blue-700"
                    />
                    Secure enterprise-grade authentication
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 space-y-3">
          <p className="text-xtnd-dark-300 text-sm">Need help accessing your account?</p>
          <p className="text-xtnd-dark-400 text-sm">
            Contact your system administrator for assistance
          </p>
        </div>
      </div>
    </div>
  );
}
